import { priceAsOf } from "@nook/client-ui"
import type { NookEmbed } from "@nook/schema"
import { Skeleton } from "@nook/ui"
import { Star } from "lucide-react"
import { lazy, Suspense, useEffect, useRef, useState } from "react"

const TweetEmbed = lazy(() => import("./tweet.js"))

/** Nothing sets `.dark` yet (`sonner.tsx`); the day something does, embeds follow. */
const theme = () => (document.querySelector(".dark") ? "dark" : "light")

/**
 * A platform nook as the platform draws it (block 43).
 *
 * Inside this component the platform's look wins over shadcn's tokens —
 * override 2 of the block, and the only place in the webapp it applies.
 * Instagram and LinkedIn render light whatever the app's theme; no stylesheet
 * reaches a cross-origin frame.
 */
export function PlatformEmbed({ embed }: { embed: NookEmbed }) {
  switch (embed.kind) {
    case "x":
      return (
        <Suspense fallback={<Skeleton className="h-64 w-full" />}>
          <TweetEmbed tweet={embed.tweet} theme={theme()} />
        </Suspense>
      )
    case "youtube":
      return (
        <iframe
          title="YouTube video"
          src={`https://www.youtube-nocookie.com/embed/${embed.videoId}${embed.start ? `?start=${embed.start}` : ""}`}
          // Without a Referer the player answers error 153 (override 9).
          referrerPolicy="strict-origin-when-cross-origin"
          allow="encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className={`border-border rounded-lg border ${
            embed.vertical ? "mx-auto block aspect-[9/16] h-[min(70vh,40rem)] max-w-full" : "aspect-video w-full"
          }`}
        />
      )
    case "instagram":
      return (
        <Frame
          title="Instagram post"
          src={`https://www.instagram.com/${embed.reel ? "reel" : "p"}/${embed.code}/embed/captioned/`}
          height={embed.reel ? 740 : 640}
        />
      )
    case "linkedin":
      return (
        <Frame
          title="LinkedIn post"
          src={`https://www.linkedin.com/embed/feed/update/${embed.urn}`}
          height={570}
        />
      )
    case "reddit":
      return (
        <Frame
          title="Reddit post"
          src={`https://embed.reddit.com/r/${embed.subreddit}/comments/${embed.postId}/?embed=true&theme=${theme()}`}
          height={480}
        />
      )
    case "tiktok":
      return <TikTok videoId={embed.videoId} />
    case "facebook":
      return (
        <Frame
          title="Facebook post"
          src={`https://www.facebook.com/plugins/${embed.video ? "video" : "post"}.php?href=${encodeURIComponent(embed.permalink)}&show_text=true&width=500`}
          height={embed.video ? 740 : 600}
        />
      )
    case "threads":
      return (
        <Frame title="Threads post" src={`https://www.threads.com/@${embed.username}/post/${embed.code}/embed`} height={560} />
      )
    case "product":
      return <Product embed={embed} />
  }
}

/** Hidden when the player has not loaded in 10s: where tiktok.com is blocked, the request hangs rather than fails. */
function TikTok({ videoId }: { videoId: string }) {
  const [state, setState] = useState<"loading" | "loaded" | "failed">("loading")

  useEffect(() => {
    const timer = setTimeout(() => setState((s) => (s === "loading" ? "failed" : s)), 10_000)
    return () => clearTimeout(timer)
  }, [])

  if (state === "failed") return null
  return (
    <iframe
      title="TikTok video"
      src={`https://www.tiktok.com/player/v1/${videoId}?rel=0`}
      allow="encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
      onLoad={() => setState("loaded")}
      className="border-border mx-auto block aspect-[9/16] h-[min(70vh,40rem)] max-w-full rounded-lg border"
    />
  )
}

/** Drawn from the save, not the store: the price is labelled with the day it was read. */
function Product({ embed }: { embed: Extract<NookEmbed, { kind: "product" }> }) {
  const price = priceAsOf(embed)

  return (
    <div className="flex gap-4 rounded-lg border p-4">
      {embed.image ? (
        <img src={embed.image} alt="" className="bg-muted size-28 shrink-0 rounded-md object-contain" />
      ) : null}
      <div className="flex min-w-0 flex-col gap-1">
        {embed.brand ? <span className="text-muted-foreground text-xs">{embed.brand}</span> : null}
        <span className="line-clamp-3 font-medium">{embed.title}</span>
        {embed.rating !== undefined ? (
          <span className="text-muted-foreground flex items-center gap-1 text-xs">
            <Star className="size-3" />
            {embed.rating}
            {embed.reviewsCount !== undefined ? ` · ${embed.reviewsCount.toLocaleString()} ratings` : null}
          </span>
        ) : null}
        <span className="text-sm">{price ?? "Price unavailable when saved"}</span>
      </div>
    </div>
  )
}

const parse = (data: unknown) => {
  try {
    return typeof data === "string" ? JSON.parse(data) : data
  } catch {
    return null
  }
}

/**
 * An iframe that grows to the height its document reports: Instagram's
 * `MEASURE` and Reddit's `resize.embed`. Neither shape was confirmed in a
 * browser when this was written (block 43 prep), so the fixed height stands
 * until a message does arrive — and LinkedIn sends none.
 *
 * Only messages from this frame's own window count: a stricter check than an
 * origin, which every other embed of the same platform would share.
 */
function Frame({ title, src, height }: { title: string; src: string; height: number }) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [measured, setMeasured] = useState<number>()

  useEffect(() => {
    const onMessage = ({ source, data }: MessageEvent) => {
      if (source !== ref.current?.contentWindow) return
      const message = parse(data)
      const next = message?.type === "MEASURE" ? message.details?.height : message?.type === "resize.embed" && message.data
      if (typeof next === "number" && next > 0) setMeasured(next)
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [])

  return (
    <iframe
      ref={ref}
      title={title}
      src={src}
      height={measured ?? height}
      className="border-border w-full rounded-lg border"
    />
  )
}
